import { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilePdf,
    faFileWord,
    faFileAudio,
    faFileVideo,
    faFile,
    faEye,
    faEyeSlash,
    faVolumeUp
} from '@fortawesome/free-solid-svg-icons';
import Image from '@mui/icons-material/Image';
import Panorama from '@mui/icons-material/Panorama';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';

export interface FileIconHoverProps {
    /** URL o nombre del archivo */
    fileName: string;
    /** Callback cuando se hace clic en el icono de visualizar */
    onView?: (fileName: string) => void;
    /** Tamaño del componente */
    size?: 'xs' | 'small' | 'medium' | 'large';
    /** Clase CSS personalizada */
    className?: string;
    /** Si debe mostrar tooltip */
    showTooltip?: boolean;
}

interface FileIconConfig {
    icon: React.ReactNode;
    color: string;
    title: string;
}

const FileIconHover: React.FC<FileIconHoverProps> = ({
    fileName,
    onView,
    size = 'medium',
    className,
    showTooltip = true
}) => {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);

    // Determinar el tamaño del icono de hover
    const eyeIconSize = size === 'xs' ? 'lg' : size === 'small' ? '2x' : size === 'medium' ? '2x' : '3x';

    // Extraer extensión del archivo 
    const getFileExtension = (filename: string): string => {
        if (!filename || typeof filename !== 'string') return '';
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop()!.toLowerCase().split(/\#|\?/)[0] : '';
    };

    /* Determinar si el archivo se puede visualizar */
    const canVisualizeFile = (filename: string): boolean => {
        if (!filename || typeof filename !== 'string') return false;
        const extension = getFileExtension(filename);
        
        // Lista completa de extensiones visualizables basada en los formatos permitidos del sistema
        const visualizableExtensions = [
            // Imágenes
            'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
            // Documentos
            'pdf', 'txt',
            // Audio
            'mp3', 'wav', 'ogg',
            // Video
            'mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'dav'
        ];
        
        return visualizableExtensions.includes(extension);
    };

    /* Obtener el icono de hover según la extensión */
    const getHoverIcon = (filename: string) => {
        if (!filename || typeof filename !== 'string') {
            return { icon: faEyeSlash, size: eyeIconSize as any };
        }

        const extension = getFileExtension(filename);

        if (extension === 'mp3') {
            return { icon: faVolumeUp, size: eyeIconSize as any };
        }

        if (!canVisualizeFile(filename)) {
            return { icon: faEyeSlash, size: eyeIconSize as any };
        }

        return { icon: faEye, size: eyeIconSize as any };
    };

    /* Obtener configuración del icono por extensión */
    const getFileIconConfig = (filename: string): FileIconConfig => {
        const extension = getFileExtension(filename);

        const iconSizeMap = {
            xs: { fa: 'lg', mui: 'small' },
            small: { fa: '2x', mui: 'medium' },
            medium: { fa: '3x', mui: 'large' },
            large: { fa: '4x', mui: 'large' }
        };

        const currentSize = iconSizeMap[size];
        const muiIconSize = size === 'xs' ? 24 : size === 'small' ? 32 : size === 'medium' ? 48 : 64;

        switch (extension) {
            case 'pdf':
                return {
                    icon: <FontAwesomeIcon icon={faFilePdf} size={currentSize.fa as any} />,
                    color: '#dc2626', // text-red-600
                    title: 'PDF'
                };
            case 'docx':
            case 'doc':
                return {
                    icon: <FontAwesomeIcon icon={faFileWord} size={currentSize.fa as any} />,
                    color: '#1e40af', // text-blue-800
                    title: 'Documento Word'
                };
            case 'mp3':
                return {
                    icon: <FontAwesomeIcon icon={faFileAudio} size={currentSize.fa as any} />,
                    color: '#2563eb', // text-blue-600
                    title: 'Audio MP3'
                };
            case 'wav':
            case 'ogg':
                return {
                    icon: <FontAwesomeIcon icon={faFileAudio} size={currentSize.fa as any} />,
                    color: '#2563eb', // text-blue-600
                    title: `Audio ${extension.toUpperCase()}`
                };
            case 'mp4':
            case 'avi':
            case 'mov':
            case 'wmv':
            case 'flv':
            case 'mkv':
            case 'webm':
                return {
                    icon: <FontAwesomeIcon icon={faFileVideo} size={currentSize.fa as any} />,
                    color: '#9333ea', // text-purple-600
                    title: `Video ${extension.toUpperCase()}`
                };
            case 'dav':
                return {
                    icon: <FontAwesomeIcon icon={faFileVideo} size={currentSize.fa as any} />,
                    color: '#9333ea', // text-purple-600
                    title: 'Video DAV (DVR)'
                };
            case 'txt':
                return {
                    icon: <FontAwesomeIcon icon={faFile} size={currentSize.fa as any} />,
                    color: '#6b7280', // text-gray-500
                    title: 'Archivo de texto'
                };
            case 'png':
                return {
                    icon: <Image sx={{ fontSize: muiIconSize }} />,
                    color: '#10b981', // text-emerald-500
                    title: 'Imagen PNG'
                };
            case 'jpg':
            case 'jpeg':
                return {
                    icon: <PhotoCamera sx={{ fontSize: muiIconSize }} />,
                    color: '#f59e0b', // text-amber-500
                    title: 'Imagen JPEG'
                };
            case 'gif':
            case 'webp':
            case 'svg':
                return {
                    icon: <Panorama sx={{ fontSize: muiIconSize }} />,
                    color: '#14b8a6', // text-teal-500
                    title: `Imagen ${extension.toUpperCase()}`
                };
            default:
                return {
                    icon: <FontAwesomeIcon icon={faFile} size={currentSize.fa as any} />,
                    color: '#9ca3af', // text-gray-400
                    title: 'Archivo'
                };
        }
    };

    const handleClick = () => {
        onView?.(fileName);
    };

    const fileConfig = getFileIconConfig(fileName);
    const hoverConfig = getHoverIcon(fileName);

    /* Determinar el tamaño del contenedor */
    const containerSizeMap = {
        xs: { width: '28px', height: '28px' },
        small: { width: '40px', height: '40px' },
        medium: { width: '80px', height: '80px' },
        large: { width: '100px', height: '100px' }
    };

    const containerSize = containerSizeMap[size];

    /* Obtener el texto del tooltip según el estado */
    const getTooltipText = (filename: string, isHovered: boolean): string => {
        if (!filename || typeof filename !== 'string') {
            return `${t('archivo.fileNotAvailable')}`;
        }

        if (isHovered) {
            const extension = getFileExtension(filename);
            if (extension === 'mp3') {
                return `${t('archivo.playAudio')}`;
            }
            if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'dav'].includes(extension)) {
                return `${t('archivo.playVideo')}`;
            }
            if (canVisualizeFile(filename)) {
                return `${t('archivo.viewFile')}`;
            }
            return `${t('archivo.fileNotViewable')}`;
        }

        return fileConfig.title;
    };

    const component = (
        <Box
            className={className}
            sx={{
                ...containerSize,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                cursor: 'pointer',
                p: 1
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
        >
            {/* Icono principal del archivo */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fileConfig.color,
                    transition: 'all 0.3s ease-in-out',
                    opacity: isHovered ? 0 : 1,
                    transform: isHovered ? 'scale(0.9)' : 'scale(1)'
                }}
            >
                {fileConfig.icon}
            </Box>

            {/* Icono de hover que aparece según el tipo de archivo */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2563eb', // text-blue-600
                    transition: 'all 0.3s ease-in-out',
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? 'scale(1)' : 'scale(0.9)'
                }}
            >
                <FontAwesomeIcon icon={hoverConfig.icon} size={hoverConfig.size as any} />
            </Box>
        </Box>
    );

    /* Envolver con Tooltip si está habilitado */
    if (showTooltip) {
        return (
            <Tooltip
                title={getTooltipText(fileName, isHovered)}
                placement="top"
                arrow
            >
                {component}
            </Tooltip>
        );
    }

    return component;
};

export default FileIconHover;